import { AlertService } from './../../shared/services/alert.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, UntypedFormArray, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { Departments } from '../../shared/enums/departments.enum';
import { IDuration, Settings, ITimeZone, HoursOfOperation } from '../../shared/models/settings.model';
import { SettingsService } from '../../shared/services/settings.service';
import * as fromActions from '../../store/settings/settings.actions';
import { SettingsState } from '../../store/settings/settings.reducer';
import * as fromSelector from '../../store/settings/settings.selectors';
import { TransportationType } from '@signal/asp-data-commons';
import {environment, countryCd, exitConfig} from '../../shared/services/util.service';
import {TranslateService} from '@ngx-translate/core';
import { SalesAvailabilityState} from '../../store/sales-availability/sales-availability.reducer';
import * as fromSalesAvailabilityActions from '../../store/sales-availability/sales-availability.actions';
import * as fromSalesAvailabilitySelector from '../../store/sales-availability/sales-availability.selectors';
import { ShopMaxPerDay } from '../../shared/models/slot-management.model';
import { SubscriptionList } from '../../shared/models/asp.types';
import * as util from 'src/app/modules/shared/services/util.service';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';


@Component({
  selector: 'app-sales-settings',
  templateUrl: './sales-settings.component.html',
  styleUrls: ['./sales-settings.component.scss']
})
export class SalesSettingsComponent implements OnInit, OnDestroy {
  department: string = Departments.SALES;

  settingsForm: UntypedFormGroup;
  settings$: Observable<Settings>;
  zipcodes$: Observable<string[]>;
  timezones$: Observable<ITimeZone[]>;
  scheduleValues: string[] = ["1 Day", "2 Days", "3 Days", "5 Days", "7 Days", "14 Days", "21 Days"];
  amenities: string[] = [];
  hasAmenities = false;
  hoursOfOperation: HoursOfOperation[] = [];
  timeDuration: IDuration[];
  subs: SubscriptionList = {};
  syncInProgress=false;
  curOffTime: IDuration[];
  notification = {
    syncFailure: true,
    appointment: false,
    delivery: false,
    testDrive:false,
    customerSlot: false
  }
  portalSwitches = {
    showEstimatedTime: true,
    enableDelivery:true,
    enableLearning:true,
    enableTestDrive:true,
    enableAmenities: true,
    lockSalesConsultant:"Lock",
    hideSalesConsultant:false,
    enableVehicleInquiry:false,
    enableTradeIn:true
  }
  detailedCapcitySetup : number = 0;
  shopMaxTotal$: Observable<ShopMaxPerDay[]>;
  weekdays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  isEnglishText : boolean = true;
  alexaEnabled: boolean = true;
  customerNotificationEnabled : boolean = false;
  reminderNotificationEnabled : boolean = false;
  tooltip :any;
  showDelivery:boolean;
  isUsDealer: boolean = countryCd.toUpperCase().includes('US');
  saveInProgress = false;

  constructor(private readonly fb: UntypedFormBuilder, private readonly settingsState: Store<SettingsState>,
    private readonly settingsService: SettingsService, private readonly alertService: AlertService, 
    private readonly translate:TranslateService,
    private readonly salesAvailabilityState: Store<SalesAvailabilityState>,
    private readonly dialog: MatDialog) {
    this.showDelivery = environment.showDelivery;
    this.setupStore();
    this.initializeSubscriptions();
    this.setupForm();
    this.tooltip={
      time: this.translate.instant('generalSettings.toUpdateTheOperationalHoursGoToTheToyotaDISsystem'),
      transport: this.translate.instant('generalSettings.setTheTransportOptionsOfferedByYourDealership'),
      customerNotification : this.translate.instant('generalSettings.enableToAllowUsersToReviceEmailNotifications'),
      reminderNotification : this.translate.instant('generalSettings.enableToAllowUsersToReviceReminderNotifications'),
      alexa: this.translate.instant('generalSettings.enableToAllowUsersToBookAppointmentsThroughAlexa'),
      portal: this.translate.instant('generalSettings.manageSettingsOntheCustomerOnlinePortal'),
      deliveryBufferDays: this.translate.instant('generalSettings.deliveryBufferDaysTooltip'),
      notifications: this.translate.instant('generalSettings.automatedEmailsWillBeSentForTheTriggerPointsYouSetHere'),
      capacity: this.translate.instant('generalSettings.toUpdateTheASPFulfilmentSlabsAndGaragesDailyFlatRateHourCapacityCalculationMethod') 
    };
     if(this.translate.currentLang?.includes('es')){
      this.isEnglishText = false;
    }
  }

  ngOnInit(): void {
    this.curOffTime = this.settingsService.getCutOffTime();
    this.timeDuration = this.settingsService.getTimeDuration(this.department);
    this.subs.shopMaxTotal = this.shopMaxTotal$?.subscribe(data=>{
      if(data && data.length > 0){
        const day: string = this.weekdays[new Date().getDay()];
        this.detailedCapcitySetup = data.find(x=> x.dayOfWeek === day).slotCapacity;
      }
      if(this.detailedCapcitySetup === 0){
        this.settingsForm.get('capacity').get('slotsCalculationMethod').patchValue("DAS");
      }
    });
    
  }
  canExit():Observable<boolean> { 
    const result = new Subject<boolean>();
    const dialogRef = this.dialog.open(ConfirmDialogComponent,exitConfig
      );
      
    this.subs.dialogRef = dialogRef.afterClosed().subscribe(event => {
    
      if (event.action === 'confirm') {
      if(this.saveChanges())
      {
      result.next(true);
      result.complete();
      }
      }
      else if(event.action === 'close'){
        result.next(true);
        result.complete();
      }
    });
    return result.asObservable(); 
  };

   // returns if the forms are edited
   formsNotEdited()
   {
     return  !this.settingsForm .dirty
   }

  setupStore() {
    const obj = {
      department: this.department,
    }
    this.settingsState.dispatch(new fromActions.LoadSettings(obj));
    this.settings$ = this.settingsState.select(fromSelector.selectSettings);

    this.settingsState.dispatch(new fromActions.LoadZipcodes(obj));
    this.settingsState.dispatch(new fromActions.LoadTimezones());
    this.zipcodes$ = this.settingsState.select(fromSelector.selectZipcodes);
    this.timezones$ = this.settingsState.select(fromSelector.selectTimezones);
    this.salesAvailabilityState.dispatch(new fromSalesAvailabilityActions.LoadShopMaxTotal());
    this.shopMaxTotal$ = this.salesAvailabilityState.select(fromSalesAvailabilitySelector.selectShopMaxTotal);
    
  }

  initializeSubscriptions() {
    this.subs.settingsLoaded = this.settingsState.select(fromSelector.selectSettingsLoaded).subscribe(loaded => {
      if (loaded) {
        this.saveInProgress = false;
      }
    });
    this.subs.settings = combineLatest(this.settings$,this.shopMaxTotal$).subscribe(([value, shopMax] )=> {
      if (value) {
        value = {
          ...value,
          portal: {
            ...value.portal,
            deliveryBufferDays: this.normalizeDeliveryBufferDays(value?.portal?.deliveryBufferDays)
          }
        };
        this.hoursOfOperation = value.time.hoursOfOperation;
        this.amenities = this.amenities?.length === 0 ? value.portal?.amenities : this.amenities;
        this.hasAmenities = !!this.amenities?.length;
        if (value.portal) {
          value.portal.enableAmenities = this.hasAmenities && Object.keys(value.portal).includes('enableAmenities') ? value.portal.enableAmenities : false;
        }
        this.settingsForm.patchValue(value);
        if(shopMax && shopMax.length > 0){
          const day: string = this.weekdays[new Date().getDay()];
          this.detailedCapcitySetup = shopMax.find(x=> x.dayOfWeek === day).slotCapacity;
        }
        if(this.detailedCapcitySetup === 0){
          this.settingsForm.get('capacity').get('slotsCalculationMethod').patchValue("DAS");
        }
        if(!this.settingsForm.get('time').get('timeZone').value){
          this.setupTimezoneValidation();
        }
        if (! +this.settingsForm.get('capacity').get('dailyAverageServiceSlots').value) {
          this.settingsForm.get('capacity').get('dailyAverageServiceSlots').markAsTouched();
          this.settingsForm.get('capacity').get('dailyAverageServiceSlots').updateValueAndValidity()
        }
        this.setupTransportOptions(value.transport.transportOptions);
        this.setupPdiChecklist(value.portal?.pdiChecklist);
        this.setSwitchValues(value);

        if(value.notification.appointment.distributionList){
          this.setupDistributionList('appointment', value.notification.appointment.distributionList);
        }

        if(value.notification.delivery.distributionList){
          this.setupDistributionList('delivery', value.notification.delivery.distributionList);
        }

        if(value.notification.testDrive.distributionList){
          this.setupDistributionList('testDrive', value.notification.testDrive.distributionList);
        }

        if(value.notification.syncFailure.distributionList){
          this.setupDistributionList('syncFailure', value.notification.syncFailure.distributionList);
        }
      }
    })
  }

  setupForm() {
    this.settingsForm = this.fb.group({
      transport: this.fb.group({
        transportOptions: this.fb.array([]),
        includedRemoteZipCodes: this.fb.control('')
       }),
      notification: this.fb.group({
        syncFailure: this.fb.group({
          enable: [true],
          distributionList: this.fb.array([])
        }),
        appointment: this.fb.group({
          enable: [false],
          distributionList: this.fb.array([])
        }),
        delivery: this.fb.group({
          enable: [false],
          distributionList: this.fb.array([])
        }),
        testDrive: this.fb.group({
          enable: [false],
          distributionList: this.fb.array([])
        }),
        customerSlot: this.fb.group({
          enable: [false],
          distributionList: this.fb.array([])
        })
      }),
      time: this.fb.group({
        slotDuration: [''],
        cutOffTime: [''],
        testdriveAppointmentDuration: [''],
        vehicleInquiryDuration: [''],
        deliveryAppointmentDuration: [''],
        deliveryApptDuration: [0],
        techcenterAppointmentDuration: [''],
        timeZone: ['', Validators.required]
      }),
      portal: this.fb.group({
        showEstimatedTime: [''],
        testDriveBannerTitleText: [''],
        testDriveBannerBodyText: [''],
        deliveryBannerTitleText: [''],
        deliveryBannerBodyText: [''],
        deliveryBufferDays: [''],
        techCenterBannerTitleText: [''],
        techCenterBannerBodyText: [''],
        enableVehicleInquiry: [false],
        vehicleInquiryBannerTitleText: [''],
        vehicleInquiryBannerBodyText: [''],
        pdiChecklist: this.fb.array([]),
        enableDelivery:[false],
        enableLearning:[true],
        enableTestDrive:[true],
        enableAmenities:[true],
        lockSalesConsultant:["Lock"],
        hideSalesConsultant:[false],
        enableTradeIn:[true]
      }),
      capacity : this.fb.group({
        dailyAverageServiceSlots : ['0',Validators.min(1)],
        slotsCalculationMethod : [''],
      }),
      alexaEnabled: [true],
      customerNotificationEnabled : [false],
      reminderNotificationEnabled: [false],
      reminderNotificationSchedule: ['']
    });

  }

  setMinError(control:string){
    this.settingsForm.controls['capacity'].get(control).setErrors({required: this.translate.instant('generalSettings.greaterThan0')})
  }
  setMaxError(control:string){
    this.settingsForm.controls['capacity'].get(control).setErrors({required: this.translate.instant('generalSettings.lesserThan100')})
  }
  ngOnDestroy() {
    util.unsubscribeSubscriptions(this.subs);
  }
  setReminderNotifValidation(){
    const reminderNotifField = (this.settingsForm.get('reminderNotificationEnabled') as UntypedFormControl);
    if (this.settingsForm.get('customerNotificationEnabled').value) {
      reminderNotifField.markAsTouched();
      reminderNotifField.updateValueAndValidity();
      reminderNotifField.enable();
    } else {
      reminderNotifField.clearValidators();
      reminderNotifField.updateValueAndValidity();
      reminderNotifField.disable();
    }
    this.setReminderScheduleValidation();
  }

  setReminderScheduleValidation(){
    const reminderScheduleField = (this.settingsForm.get('reminderNotificationSchedule') as UntypedFormControl);
    if (this.settingsForm.get('reminderNotificationEnabled').value && this.settingsForm.get('customerNotificationEnabled').value) {
      reminderScheduleField.setValidators([Validators.required]);
      reminderScheduleField.markAsTouched();
      reminderScheduleField.updateValueAndValidity();
      reminderScheduleField.enable();
    } else {
      reminderScheduleField.clearValidators();
      reminderScheduleField.updateValueAndValidity();
      reminderScheduleField.disable();
    }
  }
  setupTimezoneValidation(){
    this.settingsForm.get('time').get('timeZone').markAsTouched();
    this.settingsForm.get('time').get('timeZone').updateValueAndValidity();
  }
  
  setSwitchValues(obj) {
    this.portalSwitches.showEstimatedTime = obj.portal.showEstimatedTime;
    this.portalSwitches.enableTestDrive=Object.keys(obj.portal).includes('enableTestDrive')?obj.portal.enableTestDrive:this.portalSwitches.enableTestDrive;
    this.portalSwitches.enableDelivery = Object.keys(obj.portal).includes('enableDelivery') ? obj.portal.enableDelivery : this.portalSwitches.enableDelivery;
      if (!this.hasAmenities) {
        this.portalSwitches.enableAmenities = false;
      } else if (Object.keys(obj.portal).includes('enableAmenities')) {
        this.portalSwitches.enableAmenities = obj.portal.enableAmenities;
      } else {
        this.portalSwitches.enableAmenities = true;
      }
    this.portalSwitches.lockSalesConsultant = Object.keys(obj.portal).includes('lockSalesConsultant')?obj.portal.lockSalesConsultant:this.portalSwitches.lockSalesConsultant;
    this.portalSwitches.hideSalesConsultant = Object.keys(obj.portal).includes('hideSalesConsultant')? obj.portal.hideSalesConsultant : this.portalSwitches.hideSalesConsultant;
    this.portalSwitches.enableTradeIn = Object.keys(obj.portal).includes('enableTradeIn')? obj.portal.enableTradeIn : this.portalSwitches.enableTradeIn;
    this.portalSwitches.enableLearning=Object.keys(obj.portal).includes('enableLearning')?obj.portal.enableLearning:this.portalSwitches.enableLearning;
    this.portalSwitches.enableVehicleInquiry=Object.keys(obj.portal).includes('enableVehicleInquiry')?obj.portal.enableVehicleInquiry:this.portalSwitches.enableVehicleInquiry;
    this.notification.appointment = obj.notification.appointment.enable;
    this.notification.delivery = obj.notification.delivery.enable;
    this.notification.testDrive = obj.notification.testDrive.enable;
    this.notification.syncFailure = obj.notification.syncFailure.enable;
    this.alexaEnabled = obj.alexaEnabled;
    this.reminderNotificationEnabled = obj.reminderNotificationEnabled;
    this.customerNotificationEnabled = obj.customerNotificationEnabled;
    this.setReminderNotifValidation();
    // this.notification.customerSlot = obj.notification.customerSlot.enable;
  }

  updateSwitchValues(val: boolean, property: string) {
    switch (property) {
      case 'showEstimatedTime':
        this.settingsForm.patchValue({
          portal: {
            showEstimatedTime: val
          }
        })
        break;

      case 'lockSalesConsultant':
        const value = val ? "Lock": "Unlock";
        this.settingsForm.patchValue({
          portal: {
            lockSalesConsultant: value
          }
        })
        break;

      case 'hideSalesConsultant':
        this.settingsForm.patchValue({
          portal: {
            hideSalesConsultant: val
          }
        })
        break;

      case 'enableTradeIn':
        this.settingsForm.patchValue({
          portal: {
            enableTradeIn: val
          }
        })
        break;

      case 'syncFailure':
        this.settingsForm.patchValue({
          notification: {
            syncFailure: {
              enable: val
            }
          }
        })
        break;
      case 'appointment':
        this.settingsForm.patchValue({
          notification: {
            appointment: {
              enable: val
            }
          }
        })
        break;
      case 'delivery':
        this.settingsForm.patchValue({
          notification: {
            delivery: {
              enable: val
            }
          }
        })
        break;
      case 'testDrive':
        this.settingsForm.patchValue({
          notification: {
            testDrive: {
              enable: val
            }
          }
        })
        break;
      case 'customerSlot':
        this.settingsForm.patchValue({
          notification: {
            customerSlot: {
              enable: val
            }
          }
        })
        break;
      case 'alexaEnable':
        this.settingsForm.get('alexaEnabled').patchValue(val);
        break;
      case 'customerNotificationEnabled':
        this.settingsForm.get('customerNotificationEnabled').patchValue(val);
        if(!val){
        this.settingsForm.get('reminderNotificationEnabled').patchValue(val);
        this.reminderNotificationEnabled = false;
        }
        this.setReminderNotifValidation();
        break;
      
      case 'reminderNotificationEnabled':
        this.settingsForm.get('reminderNotificationEnabled').patchValue(val);
        this.reminderNotificationEnabled = val
        this.setReminderScheduleValidation();
        break;
      
        case 'enableTestDrive':
          this.settingsForm.patchValue({
            portal: {
              enableTestDrive: val
            }
          })
          break;
          case 'enableLearning':
            this.settingsForm.patchValue({
              portal: {
                enableLearning: val
              }
            })
            break;
            case 'enableAmenities':
              this.settingsForm.patchValue({
                portal: {
                  enableAmenities: val
                }
              })
              break;
            case 'enableDelivery':
              this.settingsForm.patchValue({
                portal: {
                  enableDelivery: val
                }
              })
            break;
            case 'enableVehicleInquiry':
              this.settingsForm.patchValue({
                portal: {
                  enableVehicleInquiry: val
                }
              })
            break;
            
            default:
            break;
    }
    this.settingsForm .markAsDirty();
  }

  setAlertConHight(portalcon) {
    const portalConHeight = 821;
    const alertConHeight = 350;
    return portalcon.clientHeight > portalConHeight ? (alertConHeight + (portalcon.clientHeight - portalConHeight)) : alertConHeight
  }

  get dailyAverageServiceSlots(){
    return this.settingsForm.get('capacity').get('dailyAverageServiceSlots').value;
  }

  get slotsCalculationMethod(){
    return this.settingsForm.get('capacity').get('slotsCalculationMethod').value;
  }
  
  onSlotCapacityMethodChange(method){
    this.settingsForm.get('capacity').get('slotsCalculationMethod').patchValue(method);
  }
  get transportOptions() {
    return (this.settingsForm.controls.transport as UntypedFormGroup).controls.transportOptions as UntypedFormArray;
  }

  get pdiChecklist() {
    return this.settingsForm.get('portal').get('pdiChecklist') as UntypedFormArray;
  }

  normalizeDeliveryBufferDays(value): number {
    if (value === null || value === undefined || `${value}`.trim() === '') {
      return null;
    }
    return Number(`${value}`.replace(/\D/g, '').slice(0, 2));
  }

  onDeliveryBufferDaysInput() {
    const bufferControl = this.settingsForm.get('portal').get('deliveryBufferDays') as UntypedFormControl;
    const normalizedValue = this.normalizeDeliveryBufferDays(bufferControl?.value);
    if (bufferControl && normalizedValue !== bufferControl.value) {
      bufferControl.setValue(normalizedValue);
    }
  }

  addChecklistItem() {
    const checklist = this.fb.control('', [Validators.required, this.noWhitespaceValidator]);
    this.pdiChecklist.push(checklist);
  }

  removeChecklistItem(i) {
    this.pdiChecklist.removeAt(i);
  }

  setupPdiChecklist(checklist) {
    this.pdiChecklist.clear();
    if (checklist) {
      checklist.forEach(item => {
        this.pdiChecklist.push(new UntypedFormControl(item))
      });
    }
  }

  // Alert Notifcation Emails
  distributionList(type: string){
    return this.settingsForm.get('notification').get(type).get('distributionList') as UntypedFormArray;
  }
  
  addDistributionList(type, value=''){
    const emailList = this.fb.control(value, [Validators.required, Validators.email, this.noWhitespaceValidator]);
    this.distributionList(type).push(emailList);
  }

  removeDistributionList(type,i){
    this.distributionList(type).removeAt(i);
  }

  setupDistributionList(type, list) {
    this.distributionList(type).clear();
    if(list){
      list.forEach(item => {
        this.distributionList(type).push(new UntypedFormControl(item))
      });
    }
  }

  public noWhitespaceValidator(control: UntypedFormControl) {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { 'whitespace': true };
  }

  isEnable(type) {
    return this.settingsForm.get('notification').get(type).get('enable').value;
  }

  setZipcodeValidation(value, event){
    const zipcodesField = (this.settingsForm.get('transport.includedRemoteZipCodes') as UntypedFormControl);
    if(event.checked && value.id === TransportationType.REMOTE){
      zipcodesField.setValidators([Validators.required]);
      zipcodesField.markAsTouched();
      zipcodesField.updateValueAndValidity();
      zipcodesField.enable();
    } else {
      zipcodesField.clearValidators();
      zipcodesField.updateValueAndValidity();
      zipcodesField.disable();
    }
  }

  setupTransportOptions(transport) {
    this.transportOptions.clear();
    transport.forEach(trans => {
      this.transportOptions.push(new UntypedFormGroup({
        id: new UntypedFormControl(trans.id),
        category: new UntypedFormControl(trans.category),
        label: new UntypedFormControl(trans.label),
        isActive: new UntypedFormControl(trans.isActive),
        comments: new UntypedFormControl(trans.comments),
        cost: new UntypedFormControl(trans.cost),
        flatRateHours: new UntypedFormControl(trans.flatRateHours),
        enableTestDriveType: new UntypedFormControl({value: trans.enableTestDriveType, disabled: true}),
        enableDeliveryType: new UntypedFormControl({value: trans.enableDeliveryType,  disabled: true}),
        enableLearningType: new UntypedFormControl({ value: trans.enableLearningType, disabled: true })
      }));
      
      if(trans.id === TransportationType.REMOTE){
        this.setZipcodeValidation(trans,{ checked: trans.isActive });
      }
    })
  }

  saveChanges() {
   if (this.settingsForm.invalid) {
      this.alertService.showAlert('otherTexts.pleaseUpdateInvalidFields');
      return false;
    }
    else {
      const formData = this.settingsForm.getRawValue();
      formData.time.hoursOfOperation = this.hoursOfOperation;
      formData.capacity = {
        dailyAverageServiceSlots : formData.capacity.dailyAverageServiceSlots,
        slotsCalculationMethod : formData.capacity.slotsCalculationMethod
      }
      const updateObj = {
        department: this.department,
        settings: formData
      };
      this.saveInProgress = true;
      this.settingsState.dispatch(new fromActions.UpdateSettings(updateObj));
      //reseting dirty value
      this.settingsForm.markAsPristine()
      return true;
    }
  }

  cancel() {
    this.settingsForm.reset();
    this.initializeSubscriptions();
  }

  getTranslation(value){
    return value ? this.translate.instant(`common.${value}`) : value ;
  }

  navigateToDIS() {
    window.open(environment.dis, "_blank");
  }

  getIntegrationText(){
    return this.translate.instant('otherTexts.syncFromDis');
  }

  syncDealerDisDetails() {
    this.runSyncWithRetry(1);
  }

  showError(){
    this.syncInProgress = false;
    this.alertService.showAlert('otherTexts.somethingWentWrongPleaseTryAgain');
  }

  private runSyncWithRetry(attempt: number) {
    this.syncInProgress = true;
    this.settingsService.syncDealerDisDetails().subscribe(res => {
      if(res.message === 'Index Updated Successfully'){
        this.syncInProgress = false;
        this.setupStore();
        this.initializeSubscriptions();
        this.alertService.showAlert('otherTexts.syncedSuccessfully', '', '', 5000, false);
        return;
      }

      this.handleSyncFailure(attempt);
    }, _error => {
      this.handleSyncFailure(attempt);
    });
  }

  private handleSyncFailure(attempt: number) {
    this.syncInProgress = false;
    if (attempt >= 3) {
      this.alertService.showEscalationAlert();
      return;
    }

    this.subs.syncRetry = this.alertService.openRetryPopup(this.translate.instant('otherTexts.somethingWentWrongPleaseTryAgain'))
      .subscribe((shouldRetry) => {
        if (shouldRetry) {
          this.runSyncWithRetry(attempt + 1);
        }
      });
  }
}
